import { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../utils/logger';
import { trackException } from '../utils/telemetry';
import { Button } from './ui';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    // Render-crash visibility in App Insights (which page/component failed).
    trackException(error, {
      source: 'ErrorBoundary',
      componentStack: errorInfo.componentStack ?? '',
      path: window.location.pathname,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback: the app's own look, in whichever theme was showing.
      return (
        <div className="min-h-screen bg-bg text-t1 flex items-center justify-center p-4" role="alert">
          <div className="max-w-[420px] w-full rounded-[24px] border border-line bg-card p-[26px] text-center wp-rise">
            <div className="w-14 h-14 rounded-full bg-acc-bg text-acc flex items-center justify-center mx-auto mb-4" aria-hidden="true">
              <svg width="26" height="26" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
              </svg>
            </div>
            <h2 className="text-[22px] font-semibold tracking-[-0.02em]">Something went wrong</h2>
            <p className="text-[14px] text-t3 mt-2 leading-[1.55]">Something unexpected happened on this page. Try again, or go back to Home.</p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="text-left mt-4 p-3 rounded-[12px] bg-fill">
                <summary className="cursor-pointer text-[13px] font-medium text-t2">Error details</summary>
                <pre className="text-[11.5px] text-acc overflow-auto mt-2 whitespace-pre-wrap">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-2.5 mt-[22px]">
              <Button variant="ghost" shape="form" size="lg" className="flex-1" onClick={() => (window.location.href = '/')}>
                Go home
              </Button>
              <Button shape="form" size="lg" className="flex-1" onClick={this.handleReset}>
                Try again
              </Button>
            </div>
            <p className="text-[12px] text-t4 mt-4">If this keeps happening, contact support.</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
